/* @flow */
/**
 * Stores Air Freight report criteria, results, subtable data, bookmark metadata, and thunk
 * workflows for searches, pagination, exports, overrides, and dropdown data.
 */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import request from '../../services/request';
import {
  toggleSpinner, updateDialogStates, togglePaginationSpinner
} from '../dialogStates/dialogStatesSlice';
import { initialAirFreightReportFieldState, airFreightReportFieldList } from '../../constants/staticData';
import {
  checkForColumnData,
  mapInputFieldsToRequest, mapRequestToInputFields, setValidFields
} from '../../helpers/dataHelpers';
import airFreightReportHeader, {
  airFreightReportHiddenHeader,
  airFreightReportPdfExportHeaderFileds
} from '../../constants/airFreightReportHeader';
import getflattenResponse, { cryptoRand } from '../../helpers/common';
import appConfig from '../../appConfig';
import { showForPROD, showForTypePayload } from '../../helpers/envServer';
import {
  getFieldsList, setDivisionCode20, searchNewFieldOnSavedSearch
} from './airFreightHelper';
import airFreightReportFieldMapping from '../../constants/fieldMappings/airFreightReportFieldMapping';
import airFreightReportFieldProperties from '../../constants/fieldProperties/airFreightReportFieldProperties';

export const name = 'airFreightReport';

function createInitialState() {
  return {
    isSavedSearchModified: false,
    airFreightReportRequestBody: null,
    airFreightReportInputFieldData: { ...initialAirFreightReportFieldState },
    airFreightReportSelectedFields: [],
    airFreightReportBookmarkName: '',
    airFreightReportBookmarkId: '',
    requestError: false,
    airFreightSubtableRequestBody: null,

    airFreightReportResultData: {},
    airFreightReportSearchId: '',
    airFreightReportTotalCount: 0,
    airFreightReportNextResults: {},
    airFreightReportPrevResults: {},
    page: 0,
    rowsPerPage: 100,
    airFreightSubtableResults: {},
    airFreightHistorySubtableResults: {},
    overrideEligibleOnly: false,
    jumpToPage: 0,
    apiCallAfterAppend: false,
    apiCallBeforeAppend: false
  };
}

export const initialState = createInitialState();

/**
 * Reducer that updates the current Air Freight report page.
 */
function updateAirFreightReportPage(state: Object, action: Object) {
  // UPDATE_AIR_FREIGHT_REPORT_PAGE
  return { ...state, page: action.payload };
}

/**
 * Reducer that updates the Air Freight report rows-per-page setting.
 */
function updateAirFreightReportRowsPerPage(state: Object, action: Object) {
  // UPDATE_AIR_FREIGHT_REPORT_ROWS_PER_PAGE
  return { ...state, rowsPerPage: action.payload };
}

/**
 * Reducer that stores the Air Freight request body and marks an active bookmark modified when the
 * request changes.
 */
function updateAirFreightReportRequestBody(state: Object, action: Object) {
  // String comparison detects bookmark changes without mutating the stored request body.
  const requestExist = JSON.stringify(state.airFreightReportRequestBody);
  // UPDATE_AIR_FREIGHT_REPORT_REQUEST_BODY
  return {
    ...state,
    airFreightReportRequestBody: action.payload,
    isSavedSearchModified: Boolean(
      state.airFreightReportBookmarkId && requestExist !== JSON.stringify(action.payload)
    )
  };
}

/**
 * Reducer that stores the Air Freight subtable request body used for detail rows.
 */
function updateAirFreightSubtableRequestBody(state: Object, action: Object) {
  // UPDATE_AIR_FREIGHT_SUBTABLE_REQUEST_BODY
  return {
    ...state,
    airFreightSubtableRequestBody: action.payload
  };
}

/**
 * Reducer that explicitly sets whether the active Air Freight bookmark has unsaved changes.
 */
function updateIsSavedSearchModified(state: Object, action: Object) {
  return {
    ...state,
    isSavedSearchModified: Boolean(action.payload)
  };
}

/**
 * Reducer that replaces the Air Freight request body without changing bookmark modification state.
 */
function updateAirFreightReportRequestBodyOnSameBookmark(state: Object, action: Object) {
  // UPDATE_AIR_FREIGHT_SEARCH_REQUEST_ON_SAME_BOOKMARK
  return {
    ...state,
    airFreightReportRequestBody: action.payload
  };
}

/**
 * Reducer that clears Air Freight result data, pagination caches, totals, and search IDs.
 */
function resetAirFreightReportResults(state: Object) {
  // RESET_AIR_FREIGHT_REPORT_RESULTS
  return {
    ...state,
    airFreightReportResultData: {},
    airFreightReportSearchId: '',
    airFreightReportTotalCount: 0,
    airFreightReportNextResults: {},
    airFreightReportPrevResults: {},
    page: 0,
    rowsPerPage: 100,
    airFreightReportSearchPanelId: ''
  };
}

/**
 * Reducer that resets Air Freight request state while preserving loaded country and plant dropdown
 * data.
 */
function resetAirFreightReportRequest(state: Object) {
  // RESET_AIR_FREIGHT_REPORT_REQUEST
  return {
    ...initialState,
    countryList: state.countryList,
    plantCodeList: state.plantCodeList
  };
}

/**
 * Reducer that clears Air Freight search criteria and bookmark metadata while preserving selected
 * fields.
 */
function resetAirFreightRequestForClearSearch(state: Object) {
  // RESET_AIR_FREIGHT_REQUEST_DATA_CLEAR_SEARCH
  return {
    ...state,
    isSavedSearchModified: false,
    airFreightReportRequestBody: null,
    airFreightReportInputFieldData: { ...initialAirFreightReportFieldState },
    airFreightReportSelectedFields: [...new Set(state.airFreightReportSelectedFields)],
    airFreightReportBookmarkName: '',
    airFreightReportBookmarkId: '',
    requestError: false
  };
}

/**
 * Reducer that stores selected Air Freight search-field keys.
 */
function updateAirFreightReportFields(state: Object, action: Object) {
  // UPDATE_AIR_FREIGHT_REPORT_SELECTED_FIELDS
  return {
    ...state, airFreightReportSelectedFields: action.payload
  };
}

/**
 * Reducer that stores Air Freight search input values.
 */
function updateAirFreightReportInputValues(state: Object, action: Object) {
  // UPDATE_AIR_FREIGHT_REPORT_INPUT_VALUES
  return { ...state, airFreightReportInputFieldData: action.payload };
}

/**
 * Reducer that stores the active Air Freight bookmark ID and clears modification tracking.
 */
function updateAirFreightReportBookmarkId(state: Object, action: Object) {
  // UPDATE_AIR_FREIGHT_REPORT_BOOKMARK_ID
  return {
    ...state,
    isSavedSearchModified: false,
    airFreightReportBookmarkId: action.payload
  };
}

/**
 * Reducer that clears active Air Freight bookmark metadata and modification tracking.
 */
function clearSavedAirFreightReport(state: Object) {
  // CLEAR_AIR_FREIGHT_REPORT_BOOKMARK
  return {
    ...state,
    isSavedSearchModified: false,
    airFreightReportBookmarkId: '',
    airFreightReportBookmarkName: ''
  };
}

/**
 * Reducer that stores the active Air Freight bookmark name and clears modification tracking.
 */
function updateairFreightReportBookmarkName(state: Object, action: Object) {
  // AIR_FREIGHT_REPORT_BOOKMARK_NAME
  return {
    ...state,
    isSavedSearchModified: false,
    airFreightReportBookmarkName: action.payload
  };
}

/**
 * Reducer that stores flattened Air Freight results, total count, search ID, and clears
 * neighboring result caches.
 */
function updateAirFreightReportResults(state: Object, action: Object) {
  // UPDATE_AIR_FREIGHT_REPORT_RESULTS
  const payload = {
    data: getflattenResponse(action.payload.data),
    dataNext: null
  };
  return {
    ...state,
    airFreightReportResultData: payload.data,
    airFreightReportTotalCount: payload.data.pages.totalResources,
    airFreightReportSearchId: cryptoRand().toString().substr(2, 8),
    selectedRecordsCount: 0,
    airFreightReportNextResults: null,
    airFreightReportPrevResults: null
  };
}

/**
 * Reducer that stores flattened Air Freight subtable results.
 */
function updateAirFreightSubtableResults(state: Object, action: Object) {
  // UPDATE_AIR_FREIGHT_SUBTABLE_RESULTS
  const payload = {
    data: getflattenResponse(action.payload),
    dataNext: null
  };
  return {
    ...state,
    airFreightSubtableResults: payload.data
  };
}

/**
 * Reducer that stores flattened Air Freight approval-history subtable results.
 */
function updateAirFreightHistorySubtableResults(state: Object, action: Object) {
  // UPDATE_AIR_FREIGHT_HISTORY_SUBTABLE_RESULTS
  const payload = {
    data: getflattenResponse(action.payload),
    dataNext: null
  };
  return {
    ...state,
    airFreightHistorySubtableResults: payload.data
  };
}

/**
 * Reducer that generates a new Air Freight search ID to refresh dependent views.
 */
function updateAirFreightReportSearchId(state: Object) {
  // UPDATE_AIR_FREIGHT_REPORT_SEARCH_ID
  return { ...state, airFreightReportSearchId: cryptoRand().toString().substr(2, 8) };
}

/**
 * Reducer that resets Air Freight input values to their initial field state.
 */
function resetAirFreightReportInputData(state: Object) {
  // RESET_AIR_FREIGHT_REPORT_INPUT_VALUES
  return {
    ...state,
    airFreightReportInputFieldData: initialState.airFreightReportInputFieldData
  };
}

/**
 * Reducer that stores the current Air Freight results and the prefetched next result set with
 * refreshed totals.
 */
function updateAirFreightReportNextResult(state: Object, action: Object) {
  // UPDATE_AIR_FREIGHT_REPORT_NEXT_RESULTS
  const payload = {
    data: getflattenResponse(action.payload.currentResultSet),
    dataNext: getflattenResponse(action.payload.response.data)
  };
  return {
    ...state,
    airFreightReportResultData: payload.data,
    airFreightReportNextResults: payload.dataNext,
    airFreightReportTotalPages: payload.data.pages.totalPages,
    airFreightReportTotalCount: payload.data.pages.totalResources
  };
}

/**
 * Reducer that stores the current Air Freight results and the prefetched previous result set with
 * refreshed totals.
 */
function updateAirFreightReportPrevResult(state: Object, action: Object) {
  // UPDATE_AIR_FREIGHT_REPORT_PREV_RESULTS
  const payload = {
    data: getflattenResponse(action.payload.currentResultSet),
    dataPrev: getflattenResponse(action.payload.response.data)
  };
  return {
    ...state,
    airFreightReportResultData: payload.data,
    airFreightReportPrevResults: payload.dataPrev,
    airFreightReportTotalPages: payload.data.pages.totalPages,
    airFreightReportTotalCount: payload.data.pages.totalResources
  };
}

/**
 * Reducer that stores Air Freight results fetched from offset zero and clears cached neighbor
 * pages.
 */
function airFreightReportOffset(state: Object, action: Object) {
  // AIR_FREIGHT_REPORT_RESULT_OFFSET
  const payload = {
    data: getflattenResponse(action?.payload?.data)
  };

  return {
    ...state,
    airFreightReportResultData: payload.data,
    airFreightReportTotalCount: payload.data.pages.totalResources,
    airFreightReportSearchId: cryptoRand().toString().substr(2, 8),
    selectedRecordsCount: 0,
    airFreightReportNextResults: null,
    airFreightReportPrevResults: null
  };
}

/**
 * Reducer that advances the visible Air Freight result page group to the prefetched next result
 * set.
 */
function ChangeReportAppendNextResultSet(state: Object) {
  // UPDATE_AIR_FREIGHT_REPORT_NEXT_APPEND
  const updatedCurrentResultSet = state.airFreightReportResultData;
  const updatedNextResultSet = state.airFreightReportNextResults;

  const payload = {
    dataPrev: updatedCurrentResultSet,
    data: updatedNextResultSet
  };

  return {
    ...state,
    airFreightReportResultData: payload.data,
    airFreightReportPrevResults: payload.dataPrev,
    airFreightReportNextResults: null
  };
}

/**
 * Reducer that advances after jump-to-page navigation and recalculates Air Freight next/previous
 * prefetch flags.
 */
function ChangeReportAppendJumpToPageNextResultSet(state: Object, action: Object) {
  // UPDATE_AIR_FREIGHT_REPORT_NEXT_APPEND
  const updatedCurrentResultSet = state.airFreightReportResultData;
  const updatedNextResultSet = state.airFreightReportNextResults;

  const payload = {
    dataPrev: updatedCurrentResultSet,
    data: updatedNextResultSet
  };

  // Page boundaries determine when another 1,000-row result window must be fetched.
  const numberString = state.page.toString();

  const rowPerPage = action.payload;

  let checkApiCallApend = false;
  let checkApiCallPrepend = false;

  if (Number(rowPerPage) === 100) {
    checkApiCallApend = numberString[numberString.length - 1] === '9';
    checkApiCallPrepend = numberString[numberString.length - 1] === '0';
  } else if (Number(rowPerPage) === 250) {
    checkApiCallApend = (Number(numberString) + 1) % 4 === 0;
    checkApiCallPrepend = Number(numberString) % 4 === 0;
  } else if (Number(rowPerPage) === 500) {
    checkApiCallApend = (Number(numberString) + 1) % 2 === 0;
    checkApiCallPrepend = Number(numberString) % 2 === 0;
  }

  return {
    ...state,
    airFreightReportResultData: payload.data,
    airFreightReportPrevResults: payload.dataPrev,
    airFreightReportNextResults: null,
    apiCallAfterAppend: checkApiCallApend,
    apiCallBeforeAppend: checkApiCallPrepend
  };
}

/**
 * Reducer that moves the visible Air Freight result page group to the prefetched previous result
 * set.
 */
function ChangeReportAppendPrevResultSet(state: Object) {
  // UPDATE_AIR_FREIGHT_REPORT_PREV_APPEND
  const updatedPrevResultSet = state.airFreightReportPrevResults;
  const updatedCurrentResultSet = state.airFreightReportResultData;

  const payload = {
    data: updatedPrevResultSet,
    dataNext: updatedCurrentResultSet
  };

  return {
    ...state,
    airFreightReportResultData: payload.data,
    airFreightReportNextResults: payload.dataNext,
    airFreightReportPrevResults: null
  };
}

/**
 * Reducer that moves backward after jump-to-page navigation and recalculates Air Freight
 * next/previous prefetch flags.
 */
function ChangeReportAppendJumpToPagePrevResultSet(state: Object, action: Object) {
  // UPDATE_AIR_FREIGHT_REPORT_PREV_APPEND
  const updatedPrevResultSet = state.airFreightReportPrevResults;
  const updatedCurrentResultSet = state.airFreightReportResultData;

  const payload = {
    data: updatedPrevResultSet,
    dataNext: updatedCurrentResultSet
  };

  const numberString = state.page.toString();

  const rowPerPage = action.payload;

  let checkApiCallApend = false;
  let checkApiCallPrepend = false;

  if (Number(rowPerPage) === 100) {
    checkApiCallApend = numberString[numberString.length - 1] === '9';
    checkApiCallPrepend = numberString[numberString.length - 1] === '0';
  } else if (Number(rowPerPage) === 250) {
    checkApiCallApend = (Number(numberString) + 1) % 4 === 0;
    checkApiCallPrepend = Number(numberString) % 4 === 0;
  } else if (Number(rowPerPage) === 500) {
    checkApiCallApend = (Number(numberString) + 1) % 2 === 0;
    checkApiCallPrepend = Number(numberString) % 2 === 0;
  }

  return {
    ...state,
    airFreightReportResultData: payload.data,
    airFreightReportNextResults: payload.dataNext,
    airFreightReportPrevResults: null,
    apiCallAfterAppend: checkApiCallApend,
    apiCallBeforeAppend: checkApiCallPrepend
  };
}

/**
 * Reducer that stores formatted country-code dropdown options.
 */
function setCountryCodeDropdown(state: Object, action: Object) {
  // SET_COUNTRY_CODE_DROPDOWN_VALUES

  return {
    ...state,
    countryList: action.payload
  };
}

/**
 * Reducer that stores formatted plant-code dropdown options.
 */
function setPlantCodeDropdown(state: Object, action: Object) {
  return {
    ...state,
    plantCodeList: action.payload
  };
}

/**
 * Reducer that stores whether searches should return override-eligible Air Freight rows only.
 */
function setOverrideEligibleOnly(state: Object, action: Object) {
  return {
    ...state,
    overrideEligibleOnly: action.payload
  };
}

/**
 * Reducer that clears the cached next Air Freight result set.
 */
function updateNextResultSettoNull(state: Object) {
  return {
    ...state,
    airFreightReportNextResults: null
  };
}

/**
 * Reducer that clears the cached previous Air Freight result set.
 */
function updatePreviousResultSettoNull(state: Object) {
  return {
    ...state,
    airFreightReportPrevResults: null
  };
}

/**
 * Reducer that stores the requested jump-to page and flags pagination thunks to honor it.
 */
function updateJumpToPage(state: Object, action: Object) {
  return {
    ...state,
    jumpToPage: action.payload,
    triggerJumpToPage: true
  };
}
/**
 * Reducer that toggles the jump-to-page trigger consumed by Air Freight pagination thunks.
 */
function updateTriggerJumpToPage(state: Object, action: Object) {
  return {
    ...state,
    triggerJumpToPage: action.payload
  };
}

/**
 * Reducer that tracks whether appending the next Air Freight page group requires another API call.
 */
function updateApiCallAfterAppend(state: Object, action: Object) {
  return {
    ...state,
    apiCallAfterAppend: action.payload
  };
}

/**
 * Reducer that tracks whether prepending the previous Air Freight page group requires another API
 * call.
 */
function updateApiCallBeforePrepend(state: Object, action: Object) {
  return {
    ...state,
    apiCallBeforeAppend: action.payload
  };
}

/**
 * Builds the Air Freight reducer map registered with Redux Toolkit.
 */
function createReducers() {
  return {
    updateAirFreightReportPage,
    updateAirFreightReportRowsPerPage,
    updateAirFreightReportRequestBody,
    updateIsSavedSearchModified,
    updateAirFreightSubtableRequestBody,
    updateAirFreightReportRequestBodyOnSameBookmark,
    resetAirFreightReportResults,
    resetAirFreightReportRequest,
    resetAirFreightRequestForClearSearch,
    updateAirFreightReportFields,
    updateAirFreightReportInputValues,
    updateAirFreightReportBookmarkId,
    clearSavedAirFreightReport,
    updateairFreightReportBookmarkName,
    updateAirFreightReportResults,
    updateAirFreightSubtableResults,
    updateAirFreightHistorySubtableResults,
    updateAirFreightReportSearchId,
    resetAirFreightReportInputData,
    updateAirFreightReportNextResult,
    updateAirFreightReportPrevResult,
    airFreightReportOffset,
    ChangeReportAppendNextResultSet,
    ChangeReportAppendPrevResultSet,
    setCountryCodeDropdown,
    setPlantCodeDropdown,
    setOverrideEligibleOnly,
    ChangeReportAppendJumpToPageNextResultSet,
    ChangeReportAppendJumpToPagePrevResultSet,
    updateJumpToPage,
    updateTriggerJumpToPage,
    updateApiCallAfterAppend,
    updateApiCallBeforePrepend,
    updatePreviousResultSettoNull,
    updateNextResultSettoNull
  };
}

export const reducers = createReducers();
export const slice = createSlice({ name, initialState, reducers });

const actions = { ...slice.actions };

/**
 * Returns a thunk that uploads an Air Freight override attachment file.
 */
function sendAttachedFile() {
  return createAsyncThunk(
    `${name}/sendAttachedFile`,
    async ({
      fileData, callback
    }, { dispatch, getState }) => {
      dispatch(toggleSpinner(true));
      request({
        api: 'uploadFileForOverride',
        method: 'fileUpload',
        data: fileData
      }, dispatch, getState).then((response) => {
        dispatch(toggleSpinner(false));
        if (callback) {
          callback(response.data);
        }
      }).catch((error) => {
        dispatch(toggleSpinner(false));
        if (callback) {
          callback(null, error);
        }
      });
    }
  );
}

/**
 * Returns a thunk that builds Air Freight search criteria, applies default AF filters and division
 * rules, calls the search API, and stores results.
 */
function fetchAirFreightReports() {
  return createAsyncThunk(
    `${name}/fetchAirFreightReports`,
    async ({
      data, callback, modifiedrequestBody, overrideEligibleOnly, inactiveFeatureFields
    }, { getState, dispatch }) => {
      dispatch(toggleSpinner(true));
      // Default request fields are assembled from visible and hidden Air Freight headers.
      const secondaryFields = [];
      getFieldsList(secondaryFields);
      const state = getState();
      let requestData = {};
      const {
        selectedColumnOrderOptions,
        rowPerPageOption: rowsPerPageOption
      } = state.searchpaneldata || {};

      // Custom column order still needs request-only fields appended for row actions.
      let columnOrderOptions = [...selectedColumnOrderOptions];
      if (checkForColumnData(state, true)) {
        airFreightReportHeader.forEach((headRow: Object) => {
          if (headRow.includeInRequest) {
            columnOrderOptions = [...columnOrderOptions, headRow.primary];
          }
        });
      }
      if (checkForColumnData(state, true)) {
        airFreightReportHiddenHeader.forEach((headerRow: Object) => {
          if (headerRow.includeInRequest && !columnOrderOptions.includes('dpomAirFreight.isLocked')) {
            columnOrderOptions.push(headerRow.primary);
          }
        });
      }
      const fieldsForRequest = checkForColumnData(state, true)
        ? columnOrderOptions
        : secondaryFields;
      if (data) {
        requestData = mapInputFieldsToRequest(data, 'airFreightReport');
        const appliedAirFreightSearchField = new Set(requestData.map((field) => field.fieldName));
        dispatch(actions.updateAirFreightReportInputValues(data));
        dispatch(actions.updateAirFreightReportFields(airFreightReportFieldList
          .map((field) => field.key)
          .filter((field) => appliedAirFreightSearchField.has(field))));
      } else {
        requestData = state.airFreightReport.airFreightReportRequestBody.search;
      }
      // Add default Air Freight-only constraints when building a new report search.
      if (Array.isArray(requestData)
        && !requestData.map((field) => field.fieldName).includes('poLine.transportationModeCode')
      ) {
        const createDate = showForPROD
          ? appConfig.airFreightPRODDate
          : appConfig.airFreightNONPRODDate;
        requestData.push(
          {
            fieldName: 'poLine.transportationModeCode',
            operator: '=',
            fieldValue: ['AF']
          },
          {
            fieldName: 'poHeader.poCreatedOn',
            operator: '>=',
            fieldValue: createDate
          },
          {
            fieldName: 'dpomAirFreight',
            function: 'IS_DEFINED'
          }
        );
      }

      // The following check is used when we search for a new field on a saved search
      requestData = searchNewFieldOnSavedSearch(requestData);

      // when overrideEligible is checked
      if (overrideEligibleOnly) {
        requestData.push(
          {
            fieldName: 'dpomAirFreight.isLocked',
            function: 'NOT IS_DEFINED'
          }
        );
      }
      const requestBody = (state.airFreightReport.airFreightReportRequestBody)
        ? {
          ...state.airFreightReport.airFreightReportRequestBody,
          search: requestData,
          offset: '0',
          ...modifiedrequestBody
        } : {
          fields: fieldsForRequest,
          search: requestData,
          count: appConfig.SearchThresholdLimit,
          offset: '0',
          ...modifiedrequestBody
        };
      const newSearch = [];
      const newSearchWithoutIsLocked = [...requestBody.search];

      // Override-eligible searches include an isLocked criterion that affects search cloning below.
      const isLocked = requestBody.search
        .map((field) => field.fieldName)
        .includes('dpomAirFreight.isLocked');

      if (isLocked) {
        requestBody.search.forEach((field) => {
          const fieldObj = { ...field };
          // 27046: when date(2024-12-12) the to date should be 2025-01-01 not 2024-12-13
          // to fix this issue on overrirde eligible commented below code
          // if (field.fieldName === 'poLine.goodsAtConsolidatorDate') {
          //   const newDt = new Date(field.fieldValue);
          //   newDt.setHours(newDt.getHours() - 12);
          //   const utcDt = new Date(newDt);
          //   fieldObj.fieldValue = `${utcDt.getUTCFullYear()}-${String(
          //     utcDt.getUTCMonth() + 1
          //   ).padStart(2, '0')}-${String(utcDt.getUTCDate() + 1).padStart(2, '0')}`;
          // }
          newSearch.push(fieldObj);
        });
      }
      let payload = { ...requestBody };
      payload = {
        ...requestBody,
        fields: [
          ...requestBody.fields,
          // Ensure locked-status helper fields are available even if not selected as columns.
          ...appConfig.includeIsLockedInAF.filter(
            (item) => !requestBody.fields.includes(item)
          )
        ],
        search: isLocked ? [...newSearch] : newSearchWithoutIsLocked
      };
      if (payload?.search) {
        // to add division: 20 in request data
        payload = {
          ...payload,
          search: setDivisionCode20(payload.search)
        };
      }
      payload = setValidFields(
        airFreightReportFieldProperties,
        payload,
        [],
        inactiveFeatureFields
      );
      request({
        api: 'airFreightReportSearch',
        method: 'post',
        data: showForTypePayload ? { ...payload, type: 'airFreight' } : payload,
        cancellable: true
      }, dispatch, getState)
        .then((response) => {
          dispatch(actions.updateAirFreightReportRequestBody(requestBody));
          dispatch(toggleSpinner(false));
          callback(response.data);
          dispatch(actions.updateAirFreightReportRowsPerPage(rowsPerPageOption));
          dispatch(actions.updateAirFreightReportResults(response));
        })
        .catch((error) => {
          dispatch(toggleSpinner(false));
          callback(null, error);
        });
    }
  );
}

/**
 * Returns a thunk that deletes an Air Freight override attachment by file location.
 */
function deleteOverrideAttachment() {
  return createAsyncThunk(
    `${name}/deleteOverrideAttachment`,
    async ({
      fileLocation, callback
    }, { dispatch, getState }) => {
      dispatch(toggleSpinner(true));
      request({
        api: 'deleteOverrideAttachment',
        method: 'delete',
        routeParams: { fileLocation }
      }, dispatch, getState).then((response) => {
        dispatch(toggleSpinner(false));
        if (callback) {
          callback(response);
        }
      }).catch((err) => {
        dispatch(toggleSpinner(false));
        if (callback) {
          callback(null, err);
        }
      });
    }
  );
}

/**
 * Returns a thunk that retrieves an override attachment SAS URL and opens it after HTTPS
 * validation.
 */
function getOverrideAttachmentFile() {
  return createAsyncThunk(
    `${name}/getOverrideAttachmentFile`,
    async ({
      location
    }, dispatch, getState) => {
      request({
        api: 'getOverrideAttachmentFile',
        routeParams: { location: location || '' },
        method: 'get'
      }, dispatch, getState).then((response) => {
        const sasUrl = response.data.attachmentSasUrl;
        // Validate URL to prevent Open Redirect vulnerability
        if (sasUrl && typeof sasUrl === 'string') {
          try {
            const url = new URL(sasUrl);
            // Only allow HTTPS protocol for security
            if (url.protocol === 'https:') {
              window.open(sasUrl);
            } else {
              console.error('Invalid URL protocol. Only HTTPS URLs are allowed.');
            }
          } catch (error) {
            console.error('Invalid URL format:', error);
          }
        } else {
          console.error('Invalid attachment URL received');
        }
      }).catch((error) => {
        console.log('error', error);
      });
    }
  );
}

/**
 * Returns a thunk that loads approval-history subtable rows for a selected Air Freight row.
 */
function fetchAirFreightHistorySubTable() {
  return createAsyncThunk(
    `${name}/fetchAirFreightHistorySubTable`,
    async ({
      rowId, callback
    }, { dispatch, getState }) => {
      dispatch(toggleSpinner(true));
      const query = rowId ? `poId=${rowId}` : '';
      request({
        api: 'airFreightApprovalHistoryTable',
        method: 'get',
        routeParams: { query },
        cancellable: true
      }, dispatch, getState)
        .then((response) => {
          callback(response.data);
          dispatch(actions.updateAirFreightHistorySubtableResults(response.data));
          dispatch(toggleSpinner(false));
        })
        .catch((error) => {
          dispatch(toggleSpinner(false));
          callback(null, error);
        });
    }
  );
}

/**
 * Returns a thunk that loads Air Freight subtable detail rows for the active report request and
 * selected row.
 */
function fetchAirFreightSubTable() {
  return createAsyncThunk(
    `${name}/fetchAirFreightSubTable`,
    async ({
      rowId, callback
    }, { getState, dispatch }) => {
      dispatch(toggleSpinner(true));
      const fields = [
        airFreightReportFieldMapping.AIR_FREIGHT_PO_NUMBER,
        airFreightReportFieldMapping.AIR_FREIGHT_ITEM_NUMBER,
        'dpomGacHistoryData'
      ];
      const state = getState();
      const requestData = state.airFreightReport.airFreightReportRequestBody.search;
      const requestBody = {
        search: requestData,
        offset: '0',
        fields: [...fields],
        count: appConfig.SearchThresholdLimit
      };
      let payload = { ...requestBody };
      if (payload?.search) {
        // to add division: 20 in request data
        payload = {
          ...payload,
          search: setDivisionCode20(payload?.search)
        };
      }
      request({
        api: 'airFreightReportSearch',
        method: 'post',
        data: showForTypePayload ? { ...payload, type: 'airFreight' } : payload,
        cancellable: true
      }, dispatch, getState)
        .then((response) => {
          dispatch(actions.updateAirFreightSubtableRequestBody(requestBody));
          dispatch(toggleSpinner(false));
          // Subtable details are scoped to the selected parent row after the shared report search.
          const filteredData = response.data.objects.filter((x) => x.id === rowId);
          callback(filteredData);
          dispatch(actions.updateAirFreightSubtableResults(filteredData));
        })
        .catch((error) => {
          dispatch(toggleSpinner(false));
          callback(null, error);
        });
    }
  );
}
/**
 * Returns a de-duplicated field list while preserving first occurrence order.
 */
function removeDuplicates(fields) {
  return Array.from(new Set(fields));
}
/**
 * Returns a thunk that builds an Air Freight export request, applies export field rules, and
 * requests a large-file export.
 */
function exportAirFreightReport() {
  return createAsyncThunk(
    `${name}/exportAirFreightReport`,
    async ({
      callback, filetype, inactiveFeatureFields
    }, { getState, dispatch }) => {
      const state = getState();
      dispatch(toggleSpinner(true));
      const searchRequestBody = {
        ...state.airFreightReport.airFreightReportRequestBody
      };
      const tempRequestData = {
        ...searchRequestBody,
        fields: Array.isArray(searchRequestBody.fields)
          ? [...searchRequestBody.fields]
          : []
      };
      if (Array.isArray(searchRequestBody.fields)
        && (searchRequestBody.fields.includes('dpomAirFreight.faf')
          || searchRequestBody.fields.includes('dpomAirFreight.naf'))) {
        tempRequestData.fields.push(...appConfig.includeFieldsAirFreight);
      }
      // Export removes helper fields that are only needed to render composite table columns.
      const removeAdditionalFields = new Set(
        airFreightReportHeader.filter((column) => column.additionalFields)
          .map((headRow) => headRow.additionalFields[0])
      );
      const filedsIncludingDuplicate = tempRequestData.fields.filter(
        (item) => !removeAdditionalFields.has(item)
      );
      const uniqueFields = removeDuplicates(filedsIncludingDuplicate);
      const duplicateKeysAre = uniqueFields.filter(
        (keys, index) => uniqueFields.indexOf(keys) !== index
      );
      const fieldToExcludes = [
        ...appConfig.excludedPOFieldsInExportedFile,
        ...airFreightReportHeader.filter((column) => column.includeInRequest)
          .map((headRow) => headRow.primary)
      ];
      let distinctAppConfigKeys = [];
      if (duplicateKeysAre.length === 0) {
        distinctAppConfigKeys = [...fieldToExcludes];
      } else {
        distinctAppConfigKeys = fieldToExcludes.filter((duplicated) => (
          !duplicateKeysAre.includes(duplicated)
        ));
      }
      const updatedField = uniqueFields.filter((field: string) => (
        !distinctAppConfigKeys.includes(field)
      ));
      const afReportPdfExportFields = airFreightReportPdfExportHeaderFileds;
      // PDF exports use the fixed PDF header field list, while other formats use visible export
      // fields.
      const checkFieldsForPdfOption = filetype === 'PDF' ? afReportPdfExportFields : updatedField;
      const airFreightReportRequestBody = showForTypePayload ? {
        search: [...(searchRequestBody.search) || []],
        fields: checkFieldsForPdfOption,
        searchType: searchRequestBody.searchType,
        fileFormat: filetype,
        type: 'airFreight'
      } : {
        search: [...(searchRequestBody.search) || []],
        fields: checkFieldsForPdfOption,
        searchType: searchRequestBody.searchType,
        fileFormat: filetype
      };
      let payload = { ...airFreightReportRequestBody };
      if (payload?.search) {
        // to add division: 20 in request data
        payload = {
          ...payload,
          search: setDivisionCode20(payload?.search)
        };
      }
      payload = setValidFields(
        airFreightReportFieldProperties,
        payload,
        [],
        inactiveFeatureFields
      );
      request({
        api: 'airFreightReportExportLargeFile',
        method: 'post',
        data: payload
      }, dispatch, getState)
        .then((response) => {
          callback(response);
          dispatch(toggleSpinner(false));
        })
        .catch((error) => {
          callback(null, error);
          dispatch(toggleSpinner(false));
        });
    }
  );
}

/**
 * Returns a thunk that executes a saved Air Freight report, restores inputs and selected fields,
 * and stores bookmark metadata.
 */
function fetchSavedAirFreightReportResults() {
  return createAsyncThunk(
    `${name}/fetchSavedAirFreightReportResults`,
    async ({
      airFreightReportRequestData, callback, inactiveFeatureFields
    }, { getState, dispatch }) => {
      const state = getState();
      // Saved report execution always starts from the first Air Freight result window.
      let requestBody = { ...airFreightReportRequestData.searchCriteria, offset: '0' };
      requestBody = setValidFields(
        airFreightReportFieldProperties,
        requestBody,
        [],
        inactiveFeatureFields
      );
      const {
        rowPerPageOption: rowsPerPageOption
      } = state.searchpaneldata || {};
      dispatch(toggleSpinner(true));
      dispatch(updateDialogStates({ executeBookmarkSpinner: true }));
      let payload = { ...requestBody };
      if (payload?.search) {
        payload = {
          ...payload,
          search: setDivisionCode20(payload?.search)
        };
      }
      request({
        api: 'airFreightReportSearch',
        method: 'post',
        data: showForTypePayload ? {
          savedSearchID: airFreightReportRequestData.id,
          ...payload,
          type: 'airFreight'
        } : {
          savedSearchID: airFreightReportRequestData.id,
          ...payload
        },
        cancellable: true
      }, dispatch, getState)
        .then((response) => {
          const fields = mapRequestToInputFields([
            ...airFreightReportRequestData.searchCriteria.search
          ]);
          const airFreightSearchFields = new Set(
            airFreightReportRequestData.searchCriteria.search.map((field) => field.fieldName)
          );
          dispatch(actions.updateAirFreightReportInputValues(
            fields
          ));
          dispatch(actions.updateAirFreightReportRequestBody(requestBody));
          dispatch(actions.updateAirFreightReportFields(airFreightReportFieldList
            .map((field) => field.key)
            .filter((field) => airFreightSearchFields.has(field))));
          dispatch(actions.updateAirFreightReportResults(response));
          dispatch(actions.updateAirFreightReportSearchId());
          dispatch(actions.updateAirFreightReportPage(0));
          dispatch(actions.updateAirFreightReportRowsPerPage(rowsPerPageOption));
          dispatch(actions.updateAirFreightReportBookmarkId(airFreightReportRequestData.id));
          dispatch(
            actions.updateairFreightReportBookmarkName(airFreightReportRequestData.name)
          );
          dispatch(toggleSpinner(false));
          dispatch(updateDialogStates({ executeBookmarkSpinner: false }));
          if (callback) {
            callback(response);
          }
        })
        .catch((error) => {
          dispatch(toggleSpinner(false));
          dispatch(updateDialogStates({ executeBookmarkSpinner: false }));
          console.log(error);
          if (callback) {
            callback(null, error);
          }
        });
    }
  );
}

/**
 * Returns a thunk that reloads Air Freight results from offset zero for the current request body.
 */
function fetchAirFreightreportsResultsOffset() {
  return createAsyncThunk(
    `${name}/fetchAirFreightreportsResultsOffset`,
    async (_, { getState, dispatch }) => {
      const state = getState();
      if (state.airFreightReport.airFreightReportRequestBody
        && state.airFreightReport.airFreightReportResultData) {
        let airFreightReportUpdatedRequest = state.airFreightReport
          .airFreightReportRequestBody;
        if (airFreightReportUpdatedRequest?.search) {
          // to add division: 20 in request data
          airFreightReportUpdatedRequest = {
            ...airFreightReportUpdatedRequest,
            search: setDivisionCode20(airFreightReportUpdatedRequest.search)
          };
        }
        airFreightReportUpdatedRequest = {
          ...airFreightReportUpdatedRequest,
          offset: '0'
        };
        dispatch(toggleSpinner(true));
        request({
          api: 'airFreightReportSearch',
          method: 'post',
          data: showForTypePayload ? { ...airFreightReportUpdatedRequest, type: 'airFreight' } : airFreightReportUpdatedRequest
        }, dispatch, getState)
          .then((response) => {
            dispatch(actions.airFreightReportOffset(response));
            dispatch(toggleSpinner(false));
            dispatch(actions.updateAirFreightReportSearchId());
          })
          .catch((error) => {
            console.log('NEXT Error ', error);
            dispatch(toggleSpinner(false));
          });
      }
      return () => false;
    }
  );
}

/**
 * Returns a thunk wrapper that dispatches the provided Air Freight offset-reset thunk.
 */
function airFreightReportDataOffset() {
  return createAsyncThunk(
    `${name}/airFreightReportDataOffset`,
    async ({
      FetchAirFreightreportsResultsOffset
    }, { dispatch }) => {
      dispatch(FetchAirFreightreportsResultsOffset());
    }
  );
}

/**
 * Returns a thunk that prefetches the next Air Freight result set for jump-to-page navigation
 * using the response cursor offset.
 */
function fetchAirFreightReportJumpToPageNextResultSet() {
  return createAsyncThunk(
    `${name}/fetchAirFreightReportJumpToPageNextResultSet`,
    async (_, { getState, dispatch }) => {
      const state = getState();
      dispatch(actions.updateApiCallAfterAppend(false));
      dispatch(togglePaginationSpinner({ isFetchingNextData: true }));
      if (state.airFreightReport?.airFreightReportRequestBody
        && state.airFreightReport?.airFreightReportResultData?.pages?.next) {
        const currentResultSet = state.airFreightReport
          .airFreightReportResultData;
        let updatedRequest = state.airFreightReport
          .airFreightReportRequestBody;
        const searchResultNextOffset = state.airFreightReport
          .airFreightReportResultData.pages.next;
        const resNext = searchResultNextOffset.indexOf(',');
        updatedRequest = {
          ...updatedRequest,
          offset: searchResultNextOffset.slice(7, resNext)
        };
        let payload = { ...updatedRequest };
        if (payload?.search) {
          // to add division: 20 in request data
          payload = {
            ...payload,
            search: setDivisionCode20(payload?.search)
          };
        }
        request({
          api: 'airFreightReportSearch',
          method: 'post',
          data: showForTypePayload ? { ...payload, type: 'airFreight' } : payload
        }, dispatch)
          .then((response) => {
            dispatch(actions.updateAirFreightReportRequestBodyOnSameBookmark(updatedRequest));
            dispatch(togglePaginationSpinner({ isFetchingNextData: false }));
            dispatch(toggleSpinner(false));
            dispatch(actions.updateAirFreightReportNextResult({ response, currentResultSet }));
          })
          .catch((error) => {
            dispatch(togglePaginationSpinner({ isFetchingNextData: false }));
            console.log('NEXT Error ', error);
          });
      }
      return () => false;
    }
  );
}

/**
 * Returns a thunk that fetches the next Air Freight result page group, including jump-to-page
 * offset handling.
 */
function fetchAirFreightReportNextResultSet() {
  return createAsyncThunk(
    `${name}/fetchAirFreightReportNextResultSet`,
    async (_, { getState, dispatch }) => {
      const state = getState();
      dispatch(togglePaginationSpinner({ isFetchingNextData: true }));
      if (state.airFreightReport?.airFreightReportRequestBody
        && state.airFreightReport?.airFreightReportResultData?.pages?.next) {
        const currentResultSet = state.airFreightReport
          .airFreightReportResultData;
        const currentJumpToPage = state.airFreightReport.jumpToPage || 0;
        // The backend returns 1,000-row windows, so rows per page determines pages per window.
        const pageGroupSize = 1000 / state.airFreightReport.rowsPerPage;
        const pageGroup = currentJumpToPage > 0
          ? Math.floor(currentJumpToPage / pageGroupSize) : 0;
        const { triggerJumpToPage } = state.airFreightReport;
        if (triggerJumpToPage) {
          dispatch(actions.updateTriggerJumpToPage(false));
        }
        let updatedRequest = state.airFreightReport
          .airFreightReportRequestBody;
        const searchResultNextOffset = state.airFreightReport
          .airFreightReportResultData.pages.next;
        const resNext = searchResultNextOffset.indexOf(',');
        updatedRequest = {
          ...updatedRequest,
          offset: triggerJumpToPage
            // Jump-to-page requests use the target 1,000-row window offset instead of the cursor.
            ? (pageGroup * 1000).toString() : searchResultNextOffset.slice(7, resNext)
        };
        let payload = { ...updatedRequest };
        if (payload?.search) {
          // to add division: 20 in request data
          payload = {
            ...payload,
            search: setDivisionCode20(payload?.search)
          };
        }
        request({
          api: 'airFreightReportSearch',
          method: 'post',
          data: showForTypePayload ? { ...payload, type: 'airFreight' } : payload
        }, dispatch)
          .then((response) => {
            dispatch(actions.updateAirFreightReportRequestBodyOnSameBookmark(updatedRequest));
            dispatch(togglePaginationSpinner({ isFetchingNextData: false }));
            dispatch(toggleSpinner(false));
            dispatch(actions.updateAirFreightReportNextResult({ response, currentResultSet }));
            if (triggerJumpToPage) {
              dispatch(actions.ChangeReportAppendJumpToPageNextResultSet(
                state.airFreightReport.rowsPerPage
              ));
              dispatch(actions.updatePreviousResultSettoNull());
            }
          })
          .catch((error) => {
            dispatch(togglePaginationSpinner({ isFetchingNextData: false }));
            console.log('NEXT Error ', error);
          });
      }
      return () => false;
    }
  );
}

/**
 * Returns a thunk wrapper that dispatches the provided next-result Air Freight pagination thunk.
 */
function ChangeReportsDataNextSet() {
  return createAsyncThunk(
    `${name}/ChangeReportsDataNextSet`,
    async ({
      FetchAirFreightReportNextResultSet
    }, { dispatch }) => {
      dispatch(FetchAirFreightReportNextResultSet());
    }
  );
}

/**
 * Returns a thunk that prefetches the previous Air Freight result set for jump-to-page navigation
 * using the response cursor offset.
 */
function fetchReportJumpToPagePrevResultSet() {
  return createAsyncThunk(
    `${name}/fetchReportJumpToPagePrevResultSet`,
    async (_, { getState, dispatch }) => {
      const state = getState();
      dispatch(actions.updateApiCallBeforePrepend(false));
      if (state.airFreightReport?.airFreightReportResultData?.pages?.prev) {
        const currentResultSet = state.airFreightReport
          .airFreightReportResultData;
        let updatedRequest = state.airFreightReport
          .airFreightReportRequestBody;
        const searchResultPrevOffset = state.airFreightReport
          .airFreightReportResultData.pages.prev;
        const res = searchResultPrevOffset.indexOf(',');
        updatedRequest = {
          ...updatedRequest,
          offset: searchResultPrevOffset.slice(7, res)
        };
        let payload = { ...updatedRequest };
        if (payload?.search) {
          // to add division: 20 in request data
          payload = {
            ...payload,
            search: setDivisionCode20(payload?.search)
          };
        }
        dispatch(togglePaginationSpinner({ isFetchingPrevData: true }));
        request({
          api: 'airFreightReportSearch',
          method: 'post',
          data: showForTypePayload ? { ...payload, type: 'airFreight' } : payload
        }, dispatch, getState)
          .then((response) => {
            dispatch(actions.updateAirFreightReportRequestBodyOnSameBookmark(updatedRequest));
            dispatch(togglePaginationSpinner({ isFetchingPrevData: false }));
            dispatch(toggleSpinner(false));
            dispatch(actions.updateAirFreightReportPrevResult({ response, currentResultSet }));
          })
          .catch((error) => {
            dispatch(togglePaginationSpinner({ isFetchingPrevData: false }));
            console.log('PREV Error ', error);
          });
      }

      return () => false;
    }
  );
}

/**
 * Returns a thunk that fetches the previous Air Freight result page group, including jump-to-page
 * offset handling.
 */
function fetchReportPrevResultSet() {
  return createAsyncThunk(
    `${name}/fetchReportPrevResultSet`,
    async (_, { getState, dispatch }) => {
      const state = getState();
      if (state.airFreightReport?.airFreightReportResultData?.pages?.prev) {
        const currentResultSet = state.airFreightReport
          .airFreightReportResultData;
        const currentJumpToPage = state.airFreightReport.jumpToPage || 0;
        const pageGroupSize = 1000 / state.airFreightReport.rowsPerPage;
        const pageGroup = currentJumpToPage > 0
          ? Math.floor(currentJumpToPage / pageGroupSize) : 0;
        const { triggerJumpToPage } = state.airFreightReport;
        if (triggerJumpToPage) {
          dispatch(actions.updateTriggerJumpToPage(false));
        }
        let updatedRequest = state.airFreightReport
          .airFreightReportRequestBody;
        const searchResultPrevOffset = state.airFreightReport
          .airFreightReportResultData.pages.prev;
        const res = searchResultPrevOffset.indexOf(',');
        updatedRequest = {
          ...updatedRequest,
          offset: triggerJumpToPage
            ? (pageGroup * 1000).toString() : searchResultPrevOffset.slice(7, res)
        };
        let payload = { ...updatedRequest };
        if (payload?.search) {
          // to add division: 20 in request data
          payload = {
            ...payload,
            search: setDivisionCode20(payload?.search)
          };
        }
        dispatch(togglePaginationSpinner({ isFetchingPrevData: true }));
        request({
          api: 'airFreightReportSearch',
          method: 'post',
          data: showForTypePayload ? { ...payload, type: 'airFreight' } : payload
        }, dispatch, getState)
          .then((response) => {
            dispatch(actions.updateAirFreightReportRequestBodyOnSameBookmark(updatedRequest));
            dispatch(togglePaginationSpinner({ isFetchingPrevData: false }));
            dispatch(toggleSpinner(false));
            dispatch(actions.updateAirFreightReportPrevResult({ response, currentResultSet }));
            if (triggerJumpToPage) {
              dispatch(actions.ChangeReportAppendJumpToPagePrevResultSet(
                state.airFreightReport.rowsPerPage
              ));
              dispatch(actions.updateNextResultSettoNull());
            }
          })
          .catch((error) => {
            dispatch(togglePaginationSpinner({ isFetchingPrevData: false }));
            console.log('PREV Error ', error);
          });
      }

      return () => false;
    }
  );
}

/**
 * Returns a thunk wrapper that dispatches the provided previous-result Air Freight pagination
 * thunk.
 */
function ChangeReportsDataPrevSet() {
  return createAsyncThunk(
    `${name}/ChangeReportsDataPrevSet`,
    async ({
      FetchReportPrevResultSet
    }, { dispatch }) => {
      dispatch(FetchReportPrevResultSet());
    }
  );
}

/**
 * Returns a thunk that submits Air Freight override data to the create-override API.
 */
function submitAirFreightCreateRequest() {
  return createAsyncThunk(
    `${name}/submitAirFreightCreateRequest`,
    async ({
      overrideData, callback
    }, { dispatch, getState }) => {
      dispatch(toggleSpinner(true));
      request({
        api: 'airFreightCreateOverrideRequest',
        method: 'post',
        data: showForTypePayload ? {
          airFreightData: overrideData,
          type: 'airFreight'
        } : { airFreightData: overrideData }
      }, dispatch, getState)
        .then((response) => {
          dispatch(toggleSpinner(false));
          callback(getflattenResponse(response.data));
        })
        .catch((error) => {
          dispatch(toggleSpinner(false));
          if (callback) {
            callback(null, error);
          }
        });
    }
  );
}

/**
 * Returns a thunk that loads GAC reason codes for Air Freight override workflows.
 */
function getGacReasonCode() {
  return createAsyncThunk(
    `${name}/getGacReasonCode`,
    async ({
      callback
    }, { dispatch, getState }) => {
      const payload = {};
      dispatch(toggleSpinner(true));
      request({
        api: 'getGACReasonCode',
        method: 'get'
      }, dispatch, getState).then((response) => {
        dispatch(toggleSpinner(false));
        payload.gacReasonCode = response.data;
        if (callback) {
          callback(response);
        }
      }).catch((error) => {
        dispatch(toggleSpinner(false));
        if (callback) {
          callback(null, error);
        }
      });
    }
  );
}

/**
 * Returns a thunk that loads country metadata and formats it for the Air Freight country dropdown.
 */
function getCountryData() {
  return createAsyncThunk(
    `${name}/getCountryData`,
    async ({ callback }, { dispatch }) => {
      dispatch(toggleSpinner(true));
      request(
        {
          api: 'METADATA',
          method: 'get'
        },
        dispatch
      )
        .then((response) => {
          const { countryList } = response.data;
          // Dropdown labels combine code and name while preserving the code as the submitted value.
          const countryCodeList = countryList?.map?.((option) => ({
            value: option.countryCode,
            text: `${option.countryCode} | ${option.countryName}`
          })) ?? [];
          dispatch(actions.setCountryCodeDropdown(countryCodeList));
          if (callback) {
            callback(response.data);
          }
          dispatch(toggleSpinner(false));
        })
        .catch((error) => {
          console.log(error);
          dispatch(toggleSpinner(false));
        });
      return true;
    }
  );
}

/**
 * Returns a thunk that loads plant-code metadata and formats it for the Air Freight plant dropdown.
 */
function getPlantCodeData() {
  return createAsyncThunk(
    `${name}/getPlantCodeData`,
    async ({ callback }, { dispatch }) => {
      dispatch(toggleSpinner(true));
      request(
        {
          api: 'GET_PLANT_CODE',
          method: 'get'
        },
        dispatch
      )
        .then((response) => {
          const { plantCodeInfo } = response.data;
          // Plant dropdown labels combine plant code and name for display.
          const plantCodeList = plantCodeInfo?.map?.((option) => ({
            id: option.plantCode,
            value: `${option.plantCode} | ${option.plantName}`
          })) ?? [];
          dispatch(actions.setPlantCodeDropdown(plantCodeList));
          if (callback) {
            callback(response.data);
          }
          dispatch(toggleSpinner(false));
        })
        .catch((error) => {
          console.log(error);
          dispatch(toggleSpinner(false));
        });
    }
  );
}

/**
 * Builds the async Air Freight action map exported by this slice.
 */
function createExtraActions() {
  return {
    sendAttachedFile: sendAttachedFile(),
    fetchAirFreightReports: fetchAirFreightReports(),
    deleteOverrideAttachment: deleteOverrideAttachment(),
    getOverrideAttachmentFile: getOverrideAttachmentFile(),
    fetchAirFreightHistorySubTable: fetchAirFreightHistorySubTable(),
    fetchAirFreightSubTable: fetchAirFreightSubTable(),
    exportAirFreightReport: exportAirFreightReport(),
    fetchSavedAirFreightReportResults: fetchSavedAirFreightReportResults(),
    fetchAirFreightreportsResultsOffset: fetchAirFreightreportsResultsOffset(),
    airFreightReportDataOffset: airFreightReportDataOffset(),
    fetchAirFreightReportNextResultSet: fetchAirFreightReportNextResultSet(),
    ChangeReportsDataNextSet: ChangeReportsDataNextSet(),
    fetchReportPrevResultSet: fetchReportPrevResultSet(),
    ChangeReportsDataPrevSet: ChangeReportsDataPrevSet(),
    submitAirFreightCreateRequest: submitAirFreightCreateRequest(),
    getGacReasonCode: getGacReasonCode(),
    getCountryData: getCountryData(),
    getPlantCodeData: getPlantCodeData(),
    fetchAirFreightReportJumpToPageNextResultSet: fetchAirFreightReportJumpToPageNextResultSet(),
    fetchReportJumpToPagePrevResultSet: fetchReportJumpToPagePrevResultSet()
  };
}

export const extraActions = createExtraActions();

export const airFreightReportActions = { ...actions, ...extraActions };
/**
 * Redux reducer for Air Freight report criteria, results, bookmark metadata, and pagination caches.
 */
export const airFreightReportReducer = slice.reducer;
